"use client";

import { useRef, useState, type FormEvent } from "react";
import {
  platform,
  type DesignPage,
  type DesignPageInventory,
  type DesignSession,
  type DesignWebsite,
} from "./api";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Input,
} from "./ui";

export type WebsitePageAction = "rename" | "publish" | "unpublish";

export function WebsitePageDetails({
  action = "rename",
  page,
  site,
  inventory,
  org,
  session,
  onClose,
  onSaved,
}: {
  action?: WebsitePageAction;
  page: DesignPage;
  site: DesignWebsite;
  inventory: DesignPageInventory;
  org: number;
  session: DesignSession;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(page.name);
  const published = action === "publish";
  const visibilitySupported = useRef(inventory.visibility_supported).current;
  const canSetVisibility = visibilitySupported && page.path !== "/";
  const visibilityChanged =
    action !== "rename" &&
    canSetVisibility &&
    published !== (page.publication_intent ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const snapshot = useRef({
    sourceVersion: inventory.source_version,
    liveRelease: inventory.live_release_id,
  });
  const lock = useRef(false);
  const intent = useRef<{ signature: string; key: string } | null>(null);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (
      lock.current ||
      !name.trim() ||
      (name.trim() === page.name && !visibilityChanged)
    )
      return;
    const body = {
      expected_source_version: snapshot.current.sourceVersion,
      expected_live_release: snapshot.current.liveRelease,
      operations: [
        {
          page_id: page.id,
          ...(action === "rename" && name.trim() !== page.name
            ? { name: name.trim() }
            : {}),
          ...(visibilityChanged ? { published } : {}),
        },
      ],
    };
    const signature = JSON.stringify(body);
    if (intent.current?.signature !== signature)
      intent.current = { signature, key: crypto.randomUUID() };
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      await platform(`websites/${site.id}/save/`, {
        method: "POST",
        org,
        csrf: session.csrf_token,
        body: { ...body, request_key: intent.current.key },
      });
      onSaved();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not save page details.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  const visibilityLabel = snapshot.current.liveRelease
    ? published
      ? "Publish"
      : "Unpublish"
    : published
      ? "Include"
      : "Exclude";
  const title =
    action === "rename" ? "Rename page" : `${visibilityLabel} ${page.name}?`;
  return (
    <Dialog
      ariaLabel={title}
      onClose={() => {
        if (!busy) onClose();
      }}
      closeOnEscape={!busy}
    >
      <form onSubmit={save}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {action === "rename" ? (
            <label>
              Name
              <Input
                autoFocus
                required
                maxLength={120}
                value={name}
                disabled={busy}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
          ) : (
            <p>
              {snapshot.current.liveRelease
                ? "This saves and publishes the current website."
                : "This updates the website preview."}{" "}
              {published
                ? "The page will be included."
                : "The page stays in Pages so you can edit and include it again."}
            </p>
          )}
          {error && <p role="alert">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            type="button"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={
              busy ||
              !name.trim() ||
              (name.trim() === page.name && !visibilityChanged)
            }
          >
            {busy
              ? "Saving…"
              : action === "rename"
                ? "Save changes"
                : visibilityLabel}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
